import React, { useState, useEffect, useMemo } from 'react';
import { Box, Button, Typography, CircularProgress } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import BasicRangeShortcuts from '../../components/date-range-picker/BasicRangeShortcuts';
import CountrySelect from '../../components/country-select/CountrySelect';
import FilterState from '../../components/country-select/FilterState';
import { setResetFilter, setDateFilter } from '../app-container/reducer/filterSlice';
import { getSignupReport } from '../../api/signupReport';
import { EMPTY_ROW, groupNcrCities } from './signupReportUtils';
import SignupSummary from './SignupSummary';
import SignupFunnelChart from './SignupFunnelChart';
import SignupGeoTable from './SignupGeoTable';
import SignupTrendChart from './SignupTrendChart';
import SignupGeoCharts from './SignupGeoCharts';
import SignupStageRates from './SignupStageRates';

const theme = createTheme({ typography: { fontFamily: 'Indivisible, sans-serif' } });

const SectionLabel = ({ children }) => (
  <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '.6px', textTransform: 'uppercase', color: '#737373', mt: 2.2, mb: 1.5 }}>
    {children}
  </Typography>
);

const SignupReport = () => {
  const dispatch = useDispatch();
  const { filter } = useSelector((state) => state);
  const selectedCountry = useSelector((state) => state.filter.country);
  const defaultDateRange = useSelector((state) => state.filter.defaultDateRange);

  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const data = await getSignupReport(filter);
      if (data) setReport(data);
    } catch (err) {
      console.error('Error loading signup report', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filter)]);

  const handleReset = () => {
    dispatch(setResetFilter());
    dispatch(setDateFilter({ dateRange: defaultDateRange }));
  };

  const periodLabel = useMemo(() => {
    const s = filter?.dateRange?.startDate ? moment(filter.dateRange.startDate) : null;
    const e = filter?.dateRange?.endDate ? moment(filter.dateRange.endDate) : null;
    if (s && e) return `between ${s.format('MMM D, YYYY')} and ${e.format('MMM D, YYYY')}`;
    return 'across all time';
  }, [filter?.dateRange]);

  const totals = report?.totals || { ...EMPTY_ROW };
  const byDate = report?.byDate || [];
  // Delhi and its satellite cities are merged into one "Delhi NCR" row for the geo
  // breakdown (table + charts). Totals are unaffected — it only regroups the same signups.
  const byCity = useMemo(() => groupNcrCities(report?.byCity || []), [report]);
  const byCountry = report?.byCountry || [];

  return (
    <ThemeProvider theme={theme}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1.5 }}>
        <Box>
          <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#000' }}>Signup Report</Typography>
          <Typography sx={{ fontSize: 13, color: '#737373', mt: 0.3 }}>
            Everyone who signed up in the selected period, grouped by their signup day and shown in their current state.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Box className="bg-light-100 d-flex align-items-center gap-3 radius-8" sx={{ border: '1px solid #d1d5db' }}>
            <CountrySelect />
            {selectedCountry ? <FilterState /> : null}
          </Box>
          <BasicRangeShortcuts id="signupReportRange" />
          <Button
            onClick={handleReset}
            sx={{
              textTransform: 'capitalize', fontSize: 14, fontWeight: 500, color: '#000', height: 40, borderRadius: '8px',
              border: '1px solid #d1d5db', backgroundColor: '#fff', transition: 'all 0.3s ease',
              '&:hover': { backgroundColor: '#BE1F1F', color: '#fff', borderColor: '#ff4A4A' },
            }}
          >
            Reset
          </Button>
        </Box>
      </Box>

      {isLoading && !report ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10 }}>
          <CircularProgress sx={{ color: '#BE1F1F' }} />
        </Box>
      ) : (
        <Box sx={{ mt: 2, opacity: isLoading ? 0.6 : 1, transition: 'opacity .2s' }}>
          <SignupSummary totals={totals} periodLabel={periodLabel} />

          <SectionLabel>Full funnel — all stages</SectionLabel>
          <SignupFunnelChart totals={totals} />

          <SignupGeoTable title="Breakdown by city" rows={byCity} nameKey="city" subKey="country" totals={totals} />
          <SignupGeoTable title="Breakdown by country" rows={byCountry} nameKey="country" totals={totals} />

          <Typography sx={{ fontSize: 11.5, color: '#A3A3A3', fontStyle: 'italic', my: 1.5 }}>
            Each signup sits in exactly one current status, attributed to its signup day, so Approved + Hidden + Inactive +
            Ghost + Deactivated + Suspended + Deleted sums to Signed Up. &quot;Active&quot; is not a status — it is Approved
            (visible on search now) + Hidden (complete, not on search), so it is not added again. The % under each number is
            its share of Signed Up; the % under Active is the completion rate of profiles still up.
          </Typography>

          <SignupTrendChart byDate={byDate} />

          <SignupGeoCharts byCity={byCity} byCountry={byCountry} />

          <SignupStageRates byDate={byDate} />
        </Box>
      )}
    </ThemeProvider>
  );
};

export default SignupReport;
